import PropTypes from 'prop-types';
import { always } from 'ramda';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Container, Typography } from '@mui/material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { FormattedMessage } from 'react-intl';
import {
  AddIcon,
  ArrowRightIcon,
  BasicCard,
  Divider,
  Document,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { Stack } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import { useGetProfileQuery } from '../../services/profile';
import { useGetMyProfileQuery } from '../../services/users';

export interface TuitionProps extends MuiBoxProps {}

export const TuitionSelection = ({ className }: TuitionProps) => {
  const screens = useAppMediaQuery();
  const navigate = useNavigate();

  const { data: user } = useGetMyProfileQuery();

  const { data: profile } = useGetProfileQuery(
    {
      userId: user?.id,
    },
    { skip: !user?.id },
  );

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  return (
    <Container sx={{ pb: { xs: 0.5, md: 0 } }}>
      <Box sx={{ py: 2 }}>
        <ArrowRightIcon
          sx={{
            width: '32px',
            height: '32px',
            transform: 'rotate(180deg)',
            color: globalColours['$global-colour-interactive-primary-default'],
            cursor: 'pointer',
          }}
          onClick={() => navigate(`/${webPaths.studentProfile}`)}
        />
      </Box>
      <Box sx={{ width: { xs: '100%', md: '568px' }, my: { xs: 3, md: 6 }, mx: 'auto' }}>
        <Typography
          variant={screens.isMobile ? 'subtitle5' : 'subtitle3'}
          sx={{ textAlign: { md: 'center' }, display: 'block', mx: 'auto' }}
        >
          Welcome, {profile?.firstname}
        </Typography>
        <Typography
          variant={screens.isMobile ? 'body3' : 'body2'}
          sx={{
            maxWidth: '500px',
            textAlign: { md: 'center' },
            display: 'block',
            mb: { xs: 3, md: 4 },
            mx: 'auto',
            color: globalColours['$global-colour-content-secondary'],
          }}
        >
          We are a community of Math enthusiasts, that includes professional tutors and passionate
          hobbyists who are here to help you.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            gap: { xs: 5, md: 1 },
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Stack
            sx={{
              flex: 0.45,
              p: 2,
              justifyContent: 'space-between',
              gap: 5,
              background: globalColours['$global-colour-background-neutral-default'],
              borderRadius: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '100%',
                background: globalColours['$global-colour-background-neutral-default'],
              }}
            >
              <AddIcon sx={{ width: '24px', height: '24px' }} />
            </Box>

            <Stack>
              <Typography variant='subtitle5'>Classity Q</Typography>
              <Typography
                variant='body3'
                sx={{ color: globalColours['$global-colour-content-secondary'] }}
              >
                Ask a math question, get FREE expert responses from members, or book a live video
                1-on-1 private tuition session!
              </Typography>
            </Stack>
          </Stack>
          <Stack sx={{ flex: 0.55, gap: 1 }}>
            <Box>
              <Typography variant='subtitle5'>Tuition centres</Typography>
              <Divider sx={{ mt: 2 }} />
              <Typography
                variant='body2'
                sx={{ color: globalColours['$global-colour-content-secondary'], mb: 2 }}
              >
                Visit one of our centres nearby for hands-on coaching and tuition.
              </Typography>
            </Box>

            <BasicCard
              title='Exam prep'
              subtitle='Coaching from expert tutors with guided materials and assessment'
              icon={
                <Document
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: globalColours['$global-colour-base-contrast'],
                  }}
                />
              }
              onClick={() => navigate(`/${webPaths.studentCountry}`)}
            />
            <BasicCard
              title='Private tuition'
              subtitle='Tutors available to help you with homework and personalised study skills'
              icon={
                <Document
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: globalColours['$global-colour-base-contrast'],
                  }}
                />
              }
              onClick={() => navigate(`/${webPaths.studentCountry}`)}
            />
          </Stack>
        </Box>
        <Box
          sx={{
            p: 1,
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: globalColours['$global-colour-interactive-primary-default'],
            cursor: 'pointer',
          }}
          onClick={() => navigate(webPaths.root)}
        >
          <Typography variant='body3'>Or continue browsing the site</Typography>
          <ArrowRightIcon sx={{ width: '16px', height: '16px' }} />
        </Box>
      </Box>
    </Container>
  );
};

TuitionSelection.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  nextScreen: PropTypes.func,
};

TuitionSelection.defaultProps = {
  children: null,
  className: null,
  nextScreen: always,
};

export default styled<any>(TuitionSelection)(
  ({ theme }) => `
  `,
);
